import React from 'react'
import {
  Users,
  ClipboardCheck,
  TrendingUp,
  AlertCircle,
  Calendar,
  UserPlus,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { PageHeader } from '@/components/ui/PageHeader'
import { Stat } from '@/components/ui/Stat'
import { Card } from '@/components/ui/Card'
import { Avatar } from '@/components/ui/Avatar'
import { formatDate, formatCurrency, formatRelativeTime } from '@/lib/utils'
import { useDashboardStats } from '@/lib/api/dashboard'
import { useFinanceSummary } from '@/lib/api/finance'

const CustomTooltip: React.FC<{ active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 shadow-card-lg text-sm">
      <p className="font-semibold text-slate-700 dark:text-slate-300 mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="tabular-nums" style={{ color: p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  )
}

export const DashboardPage: React.FC = () => {
  const today = formatDate(new Date(), 'dd MMMM yyyy, EEEE')
  const { data: stats, isLoading } = useDashboardStats()
  const { data: financeSummary } = useFinanceSummary('this_year')

  const chartData = financeSummary?.monthlyBreakdown?.map(m => ({
    ay: m.monthName.slice(0, 3),
    gelir: m.income,
    gider: m.expense,
  })) ?? []

  return (
    <div className="page-container">
      <PageHeader
        title="Dashboard"
        description={today}
      />

      {/* ── Stats Row ──────────────────────────────────── */}
      <section aria-label="Özet istatistikler">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <Stat
            icon={<Users />}
            label="Toplam Öğrenci"
            value={isLoading ? '—' : String(stats?.totalStudents ?? 0)}
            color="blue"
            loading={isLoading}
          />
          <Stat
            icon={<ClipboardCheck />}
            label="Bugünkü Yoklama"
            value={isLoading ? '—' : String(stats?.todayAttendance ?? 0)}
            color="green"
            loading={isLoading}
          />
          <Stat
            icon={<TrendingUp />}
            label="Bu Ay Tahsilat"
            value={isLoading ? '—' : formatCurrency(stats?.monthlyRevenue ?? 0)}
            color="purple"
            loading={isLoading}
          />
          <Stat
            icon={<AlertCircle />}
            label="Bekleyen Ödeme"
            value={isLoading ? '—' : String(stats?.pendingPaymentsCount ?? 0)}
            color="orange"
            loading={isLoading}
          />
        </div>
      </section>

      {/* ── Middle Row: Chart + Upcoming Lessons ────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5">
        {/* Revenue chart */}
        <Card
          title="Aylık Gelir / Gider"
          description="Bu yılın gelir ve gider karşılaştırması"
          className="xl:col-span-2"
        >
          {/* Net balance summary */}
          {financeSummary && (
            <div className="flex items-center gap-3 mb-4 mt-1 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800">
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Gelir</span>
                <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{formatCurrency(financeSummary.totalIncome)}</span>
              </div>
              <span className="text-slate-300 dark:text-slate-600 font-bold">−</span>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Gider</span>
                <span className="text-sm font-bold text-amber-700 dark:text-amber-300 tabular-nums">{formatCurrency(financeSummary.totalExpense)}</span>
              </div>
              <span className="text-slate-300 dark:text-slate-600 font-bold">=</span>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${(financeSummary.net ?? 0) >= 0 ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800' : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800'}`}>
                <span className={`text-xs font-medium ${(financeSummary.net ?? 0) >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>Kalan</span>
                <span className={`text-sm font-bold tabular-nums ${(financeSummary.net ?? 0) >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-red-700 dark:text-red-300'}`}>{formatCurrency(financeSummary.net ?? 0)}</span>
              </div>
            </div>
          )}
          <div className="h-72 mt-2" aria-label="Aylık gelir gider grafiği">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData.length > 0 ? chartData : [
                  { ay: 'Oca', gelir: 0, gider: 0 }, { ay: 'Şub', gelir: 0, gider: 0 },
                  { ay: 'Mar', gelir: 0, gider: 0 }, { ay: 'Nis', gelir: 0, gider: 0 },
                  { ay: 'May', gelir: 0, gider: 0 },
                ]}
                margin={{ top: 4, right: 8, left: -8, bottom: 0 }}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="ay" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => v >= 1000 ? `₺${(v / 1000).toFixed(0)}K` : `₺${v}`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9', radius: 4 }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} iconType="square" iconSize={10} />
                <Bar dataKey="gelir" name="Gelir" fill="#0ea5e9" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="gider" name="Gider" fill="#f59e0b" radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Upcoming lessons */}
        <Card
          title="Yaklaşan Dersler"
          description="Önümüzdeki 7 gün"
          actions={<a href="/dersler" className="text-xs text-primary-600 hover:underline">Tümü</a>}
        >
          <ul className="mt-2 space-y-3" aria-label="Yaklaşan dersler listesi">
            {(stats?.upcomingLessons ?? []).length === 0 ? (
              <li className="text-sm text-slate-400 text-center py-4">Yaklaşan ders yok</li>
            ) : (
              (stats?.upcomingLessons ?? []).map((lesson) => (
                <li
                  key={lesson.id}
                  className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-700/30 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors"
                >
                  <div className="flex-shrink-0 h-9 w-9 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                      {lesson.groupName ?? lesson.title ?? 'Ders'}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {formatDate(lesson.startTime, 'dd MMM HH:mm')} · {lesson.trainerName ?? '—'}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 tabular-nums flex-shrink-0">
                    {lesson.studentCount}/{lesson.maxCapacity}
                  </span>
                </li>
              ))
            )}
          </ul>
        </Card>
      </div>

      {/* ── Bottom Row: Recent Students + Pending Payments ─ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent Students */}
        <Card
          title="Son Kayıtlar"
          description="Son eklenen öğrenciler"
          actions={<a href="/ogrenciler" className="text-xs text-primary-600 hover:underline">Tümü</a>}
        >
          <ul className="mt-2 space-y-3" aria-label="Son kayıtlı öğrenciler">
            {(stats?.recentStudents ?? []).length === 0 ? (
              <li className="text-sm text-slate-400 text-center py-4">Henüz öğrenci yok</li>
            ) : (
              (stats?.recentStudents ?? []).map((s) => (
                <li key={s.id} className="flex items-center gap-3">
                  <Avatar name={`${s.firstName} ${s.lastName}`} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                      {s.firstName} {s.lastName}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <UserPlus className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">
                      {formatRelativeTime(s.registrationDate ?? s.createdAt)}
                    </span>
                  </div>
                </li>
              ))
            )}
          </ul>
        </Card>

        {/* Pending Payments summary */}
        <Card
          title="Bekleyen Ödemeler"
          description="Tahsilat durumu"
          actions={<a href="/odemeler" className="text-xs text-primary-600 hover:underline">Tümü</a>}
        >
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Bekleyen İşlem</p>
                <p className="text-2xl font-bold text-amber-900 dark:text-amber-200 mt-1">
                  {isLoading ? '—' : stats?.pendingPaymentsCount ?? 0}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-amber-400" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800">
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300">Toplam Tutar</p>
                <p className="text-2xl font-bold text-red-900 dark:text-red-200 mt-1">
                  {isLoading ? '—' : formatCurrency(stats?.pendingPaymentsAmount ?? 0)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-red-400" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
